import type {GetFullContentResponse} from '@interfaces/PreviewWidget';
import {getRefElement} from '@utils/refUtils';
import {htmlDecode} from '@utils/stringUtils';
import type {ReactElement, ReactNode} from 'react';
import {useEffect, useRef, useState} from 'react';

interface Props {
    getFullContent(): Promise<GetFullContentResponse>;
    children(afterContentRetrieved: (content: string) => void): ReactNode;
}

export default function Preview(props: Props): ReactElement<Props> | null {
    const ref = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isHidden, setIsHidden] = useState(false);

    useEffect(() => {
        props
            .getFullContent()
            .then((response: GetFullContentResponse) => {
                afterContentRetrieved(response.data.content);
            })
            .catch((error: unknown) => {
                setIsLoading(false);
                console.error(error);
            });
    }, []);

    function afterContentRetrieved(content: string) {
        setIsLoading(false);
        replaceFullContent(content);
        setIsHidden(true);
    }

    function replaceFullContent(content: string) {
        const contentContainer = getContentContainer();
        if (contentContainer) {
            contentContainer.innerHTML = htmlDecode(content.trim());
        }
        document.dispatchEvent(new CustomEvent('content-load'));
    }

    function getContentContainer(): Element | null {
        try {
            const postContainer = getRefElement(ref).closest('article');
            const contentContainer = postContainer
                ?.getElementsByClassName('js-text-content')
                .item(0);
            if (contentContainer) {
                return contentContainer;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            // probably ref element not found
        }
        return null;
    }

    if (isHidden) {
        return null;
    }

    return (
        <div className="preview-widget" ref={ref}>
            <div className="preview-widget__shadow" />
            {isLoading ? <div className="loading-spinner" /> : null}
            {!isLoading && props.children(afterContentRetrieved)}
        </div>
    );
}
